import { useRef, useState } from 'react';

interface Props {
  label: string;
  hint?: string;
  value: string;
  tone?: 'default' | 'warn';
}

export default function CopyField({ label, hint, value, tone = 'default' }: Props) {
  const [copied, setCopied] = useState(false);
  const link = useRef<HTMLAnchorElement>(null);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /*
       * The clipboard API is blocked outside a secure context, which is exactly
       * the plain-http LAN setup a host tries this on first. Select the URL so
       * Ctrl+C still works - dragging to select it by hand is awkward now that
       * it is a link, and this is the only copy of the wall's credentials.
       */
      const node = link.current;
      if (!node) return;
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }

  return (
    <div
      className="panel p-4"
      style={tone === 'warn' ? { borderColor: 'color-mix(in oklab, var(--color-sulphur) 45%, var(--color-edge))' } : undefined}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="eyebrow">{label}</span>
        <button type="button" onClick={copy} className="text-sm text-petal hover:underline">
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {/*
        A new tab, every time. These fields hand out the only copy of a wall's
        credentials, and the page showing them is often unrecoverable - /created
        keeps its links in history state, so navigating away can lose them.
      */}
      <a
        ref={link}
        href={value}
        target="_blank"
        rel="noreferrer"
        className="mt-2 block font-mono text-sm break-all text-chalk/90 underline-offset-2 hover:text-petal hover:underline"
      >
        {value}
      </a>
      {hint ? <p className="mt-2 text-xs text-muted">{hint}</p> : null}
    </div>
  );
}
